import { PDFDocument, StandardFonts } from 'pdf-lib';
import { getItems, FIG_LISTS } from '../../api/figApi';
import { shouldPopulateTargetCarrier } from './acordTransactionStatus';
import { formatIsoToAcord, normalizeToIsoDate } from '../../utils/dateNormalize';
import { isCurrentPolicy, sortByEffectiveDesc } from '../../utils/policySelect';

const BLANK_PDF_URL = '/acord/ACORD_125_blank.pdf';

/**
 * Fetch the blank ACORD 125 PDF, fill in fields from insured/agency/policy data
 * plus SharePoint lists (contacts, buildings, general questions, carrier history,
 * loss history), then trigger a browser download.
 */
export default async function generateACORD125({ insured, agency, policy, transactionStatus, getToken, checkedLOBs = [] }) {
  // 1. Fetch the blank PDF
  const res = await fetch(BLANK_PDF_URL);
  if (!res.ok) throw new Error(`Failed to load blank ACORD 125 (${res.status})`);
  const pdfBytes = await res.arrayBuffer();

  // 2. Load with pdf-lib
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const form = pdfDoc.getForm();

  // 3. Log ALL field names — critical for mapping the rest later
  const fields = form.getFields();
  console.log(`[ACORD 125] Total form fields: ${fields.length}`);
  console.log('[ACORD 125] All field names:');
  fields.forEach(field => {
    console.log(`  ${field.constructor.name}: "${field.getName()}"`);
  });

  // Embed a standard font for appearance generation
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  // Today's date in MM/DD/YYYY
  const now = new Date();
  const today = `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${now.getFullYear()}`;

  // --- Helpers ---
  function setTextField(name, value) {
    try {
      const field = form.getTextField(name);
      field.setText(value);
      try { field.updateAppearances(font); } catch {}
      console.log(`[ACORD 125] Filled "${name}" = "${value}"`);
    } catch (err) {
      console.warn(`[ACORD 125] Text field not found: "${name}"`, err.message);
    }
  }

  function setCheckbox(name) {
    try {
      const field = form.getCheckBox(name);
      field.check();
      console.log(`[ACORD 125] Checked "${name}"`);
    } catch (err) {
      console.warn(`[ACORD 125] Checkbox not found: "${name}"`, err.message);
    }
  }

  // Map an InterestType string (potentially compound, e.g. "Mortgagee and Loss
  // Payee") to one or more PDF role-checkbox field names. Uses substring
  // matching so compound roles fire multiple checkboxes — the intake's radio
  // group can't capture compound roles but SharePoint allows free-text values
  // and the PDF supports multiple checkboxes per row.
  //
  // Order matters: leaseback / co-owner / owner all contain the substring
  // "owner", so we check the more specific forms first via if/else if.
  function interestTypeToCheckboxNames(interestType) {
    if (!interestType || typeof interestType !== 'string') return [];
    const t = interestType.toLowerCase();
    const matches = [];
    if (t.includes("lender's loss payable") || t.includes('lenders loss payable')) matches.push('AdditionalInterest_Interest_LendersLossPayableIndicator_A');
    if (t.includes('loss payee')) matches.push('AdditionalInterest_Interest_LossPayeeIndicator_A');
    if (t.includes('mortgagee')) matches.push('AdditionalInterest_Interest_MortgageeIndicator_A');
    if (t.includes('lienholder') || t.includes('lien holder')) matches.push('AdditionalInterest_Interest_LienholderIndicator_A');
    if (t.includes('additional insured')) matches.push('AdditionalInterest_Interest_AdditionalInsuredIndicator_A');
    if (t.includes('leaseback')) matches.push('AdditionalInterest_Interest_LeasebackOwnerIndicator_A');
    else if (t.includes('co-owner') || t.includes('co owner')) matches.push('AdditionalInterest_Interest_CoOwnerIndicator_A');
    else if (t.includes('owner')) matches.push('AdditionalInterest_Interest_OwnerIndicator_A');
    if (t.includes('trustee')) matches.push('AdditionalInterest_Interest_TrusteeIndicator_A');
    if (t.includes('registrant')) matches.push('AdditionalInterest_Interest_RegistrantIndicator_A');
    if (t.includes('breach of warranty')) matches.push('AdditionalInterest_Interest_BreachOfWarrantyIndicator_A');
    if (t.includes('employee as lessor') || t.includes('employee lessor')) matches.push('AdditionalInterest_Interest_EmployeeAsLessorIndicator_A');
    return matches;
  }

  // [Fix #5b] Q2 dispatcher — formal safety program. Intake stores selected
  // sub-options as a comma-separated set in Explanation (e.g. "Safety Manual,
  // Monthly Meetings"). Replaces the prior fragile keyword scan with
  // deterministic comma-split parsing. "Other: <text>" tokens tick the Other
  // indicator and write the trailing prose to OtherDescription_B.
  function writeQ2SafetyProgramFromExplanation(explanation) {
    if (!explanation || typeof explanation !== 'string') return;
    const tokens = explanation.split(',').map(t => t.trim().toLowerCase());
    const tokenMap = {
      'safety manual': 'CommercialPolicy_FormalSafetyProgram_SafetyManualIndicator_A',
      'safety position': 'CommercialPolicy_FormalSafetyProgram_SafetyPositionIndicator_B',
      'monthly meetings': 'CommercialPolicy_FormalSafetyProgram_MonthlyMeetingsIndicator_B',
      'osha': 'CommercialPolicy_FormalSafetyProgram_OSHAIndicator_B',
    };
    let otherText = '';
    for (const tok of tokens) {
      if (!tok) continue;
      if (tokenMap[tok]) {
        setCheckbox(tokenMap[tok]);
      } else if (tok.startsWith('other:') || tok.startsWith('other ')) {
        setCheckbox('CommercialPolicy_FormalSafetyProgram_OtherIndicator_B');
        otherText = tok.replace(/^other[:\s]+/i, '').trim();
      } else if (tok === 'other') {
        setCheckbox('CommercialPolicy_FormalSafetyProgram_OtherIndicator_B');
      }
    }
    if (otherText) {
      setTextField('CommercialPolicy_FormalSafetyProgram_OtherDescription_B', otherText);
    }
  }

  // [Fix #5b] Q5 dispatcher — cancellation/decline/non-renewal. Intake stores
  // a pipe-delimited two-part Explanation: "<reason-set>|<free-text>". Reason
  // set is a comma-separated list of reason labels mapping to the four
  // CancelNonRenew_*Indicator_A checkboxes plus Other. Free-text portion goes
  // into OtherDescription_A or UnderwritingConditionCorrectedDescription_A
  // depending on which reason was ticked; otherwise dropped with a warning.
  function writeQ5CancellationFromExplanation(explanation) {
    if (!explanation || typeof explanation !== 'string') return;
    const parts = explanation.split('|');
    const reasonSet = (parts[0] || '').toLowerCase();
    const freeText = (parts[1] || '').trim();
    const reasonMap = {
      'non-payment': 'CancelNonRenew_NonPaymentIndicator_A',
      'nonpayment': 'CancelNonRenew_NonPaymentIndicator_A',
      'non-renewal': 'CancelNonRenew_NonRenewalIndicator_A',
      'nonrenewal': 'CancelNonRenew_NonRenewalIndicator_A',
      'agent no longer represents carrier': 'CancelNonRenew_AgentNoLongerWritesForInsurerIndicator_A',
      'agent no longer writes': 'CancelNonRenew_AgentNoLongerWritesForInsurerIndicator_A',
      'underwriting condition corrected': 'CancelNonRenew_UnderwritingConditionCorrectedIndicator_A',
      'other': 'CancelNonRenew_OtherIndicator_A',
    };
    const reasons = reasonSet.split(',').map(r => r.trim()).filter(Boolean);
    const tickedReasons = [];
    for (const r of reasons) {
      if (reasonMap[r]) {
        setCheckbox(reasonMap[r]);
        tickedReasons.push(r);
      }
    }
    if (freeText) {
      if (tickedReasons.includes('other')) {
        setTextField('CancelNonRenew_OtherDescription_A', freeText);
      } else if (tickedReasons.includes('underwriting condition corrected')) {
        setTextField('CancelNonRenew_UnderwritingConditionCorrectedDescription_A', freeText);
      } else {
        console.warn(`[ACORD 125] Q5: free-text portion "${freeText}" had no matching destination field given reasons [${tickedReasons.join(', ')}]; dropped.`);
      }
    }
  }

  // ================================================================
  // PAGE 1: Producer, Carrier/Policy, Named Insured, LOBs
  // ================================================================

  // Populate the top-of-form CARRIER / NAIC / POLICY NUMBER only when this is
  // a single-LOB renewal going back to the incumbent. Multi-LOB season shops,
  // quotes, and mid-term changes leave the header blank so the application
  // reads as a fresh submission to each target carrier. (Per CRC guidance.)
  const lobSet = new Set(checkedLOBs);
  const showCarrier = shouldPopulateTargetCarrier(transactionStatus, lobSet);

  // --- Text fields ---
  const fills = {
    // Producer
    'Producer_FullName_A': agency?.AgencyName || '',
    'Producer_MailingAddress_LineOne_A': agency?.AgencyAddress || '',
    'Producer_MailingAddress_CityName_A': agency?.AgencyCity || '',
    'Producer_MailingAddress_StateOrProvinceCode_A': agency?.AgencyState || '',
    'Producer_MailingAddress_PostalCode_A': agency?.AgencyZip || '',
    'Producer_ContactPerson_FullName_A': agency?.ContactName || '',
    'Producer_ContactPerson_PhoneNumber_A': agency?.ContactPhone || '',
    'Producer_ContactPerson_EmailAddress_A': agency?.ContactEmail || '',
    'Producer_FaxNumber_A': agency?.ContactFax || '',
    'Producer_AuthorizedRepresentative_FullName_A': agency?.ProducerName || '',
    // Agency Customer ID — FIG-{SharePoint id}. Template has multiple widgets
    // sharing this parent-field name; a single write propagates to all.
    'Producer_CustomerIdentifier_A': insured?.id ? `FIG-${insured.id}` : '',

    // Carrier / Policy (top of form — gated by transactionStatus)
    'Insurer_FullName_A': showCarrier ? (policy?.Carrier || '') : '',
    'Insurer_NAICCode_A': showCarrier ? (policy?.NAICCode || '') : '',
    'Policy_PolicyNumberIdentifier_A': showCarrier ? (policy?.PolicyNumber || '') : '',
    'Insurer_ProductDescription_A': policy?.ProgramName || '',
    'Insurer_ProductCode_A': policy?.ProgramCode || '',
    'Insurer_Underwriter_FullName_A': policy?.Underwriter || '',
    'Insurer_Underwriter_OfficeIdentifier_A': policy?.UnderwriterOffice || '',
    // State is ISO; ACORD convention is MM/DD/YYYY. Normalize first in case
    // a legacy MM/DD/YYYY value made it through without going via loadPolicyForm.
    'Policy_EffectiveDate_A': formatIsoToAcord(normalizeToIsoDate(policy?.EffectiveDate)),
    'Policy_ExpirationDate_A': formatIsoToAcord(normalizeToIsoDate(policy?.ExpirationDate)),
    'Policy_Payment_EstimatedTotalAmount_A': policy?.Premium != null ? String(policy.Premium) : '',

    // Applicant / Named Insured
    'NamedInsured_FullName_A': insured?.CompanyName || '',
    'NamedInsured_TaxIdentifier_A': insured?.FEIN || '',
    'NamedInsured_MailingAddress_LineOne_A': insured?.MailingAddress || '',
    'NamedInsured_MailingAddress_CityName_A': insured?.MailingCity || '',
    'NamedInsured_MailingAddress_StateOrProvinceCode_A': insured?.MailingState || '',
    'NamedInsured_MailingAddress_PostalCode_A': insured?.MailingZip || '',
    'NamedInsured_GeneralLiabilityCode_A': insured?.GLCode || '',
    'NamedInsured_SICCode_A': insured?.SICCode || '',
    'NamedInsured_NAICSCode_A': insured?.NAICSCode || '',
    'NamedInsured_Primary_PhoneNumber_A': insured?.BusinessPhone || '',
    'NamedInsured_Primary_WebsiteAddress_A': insured?.WebsiteAddress || '',

    // [FIX #10] Date Business Started (was mapped — confirming field name)
    'NamedInsured_BusinessStartDate_A': insured?.DateBusinessStarted || '',

    // Description of Primary Operations
    'CommercialPolicy_OperationsDescription_A': insured?.OperationsDescription || '',

    // No. of Members (LLC members/managers, NOT total employees)
    'NamedInsured_LegalEntity_MemberManagerCount_A': insured?.NumberOfMembers != null ? String(insured.NumberOfMembers) : '',

    // Form date
    'Form_CompletionDate_A': today,
  };

  for (const [name, value] of Object.entries(fills)) {
    setTextField(name, value);
  }

  // --- Checkboxes ---

  // Transaction status checkbox — keys match the radio labels in the intake
  // form (ACORD_TRANSACTION_STATUSES). Uses transactionStatus param, NOT the
  // SharePoint lifecycle Status field.
  const statusMap = {
    'Quote': 'Policy_Status_QuoteIndicator_A',
    'Issue Policy': 'Policy_Status_IssueIndicator_A',
    'Bound': 'Policy_Status_BoundIndicator_A',
    'Renew': 'Policy_Status_RenewIndicator_A',
    'Change': 'Policy_Status_ChangeIndicator_A',
    'Cancel': 'Policy_Status_CancelIndicator_A',
  };
  if (transactionStatus && statusMap[transactionStatus]) {
    setCheckbox(statusMap[transactionStatus]);
  }

  // Billing
  if (policy?.AgencyBilled === true) {
    setCheckbox('Policy_Payment_ProducerBillIndicator_A');
  } else if (policy?.AgencyBilled === false) {
    setCheckbox('Policy_Payment_DirectBillIndicator_A');
  }

  // Entity type
  const entityMap = {
    'Corporation': 'NamedInsured_LegalEntity_CorporationIndicator_A',
    'Individual': 'NamedInsured_LegalEntity_IndividualIndicator_A',
    'LLC': 'NamedInsured_LegalEntity_LimitedLiabilityCorporationIndicator_A',
    'Partnership': 'NamedInsured_LegalEntity_PartnershipIndicator_A',
    'Joint Venture': 'NamedInsured_LegalEntity_JointVentureIndicator_A',
    'Not For Profit Org': 'NamedInsured_LegalEntity_NotForProfitIndicator_A',
    'Subchapter S Corporation': 'NamedInsured_LegalEntity_SubchapterSCorporationIndicator_A',
    'Trust': 'NamedInsured_LegalEntity_TrustIndicator_A',
  };
  if (insured?.EntityType && entityMap[insured.EntityType]) {
    setCheckbox(entityMap[insured.EntityType]);
  }

  // Lines of Business checkboxes — uses checkedLOBs from page component
  // NOTE: WC is NOT on ACORD 125 (it has its own ACORD 130 form)
  const lobCheckboxMap = {
    'Commercial General Liability': 'Policy_LineOfBusiness_CommercialGeneralLiability_A',
    'General Liability': 'Policy_LineOfBusiness_CommercialGeneralLiability_A',
    'Commercial Property': 'Policy_LineOfBusiness_CommercialProperty_A',
    'Inland Marine': 'Policy_LineOfBusiness_CommercialInlandMarineIndicator_A',
    'Crime': 'Policy_LineOfBusiness_CrimeIndicator_A',
    'Commercial Auto': 'Policy_LineOfBusiness_BusinessAutoIndicator_A',
    'Garage': 'Policy_LineOfBusiness_GarageAndDealersIndicator_A',
    'Umbrella': 'Policy_LineOfBusiness_UmbrellaIndicator_A',
    'Excess': 'Policy_LineOfBusiness_Excess_A',
    'Professional Liability': 'Policy_LineOfBusiness_ProfessionalLiability_A',
  };

  const lobPremiumMap = {
    'Commercial General Liability': 'GeneralLiabilityLineOfBusiness_TotalPremiumAmount_A',
    'General Liability': 'GeneralLiabilityLineOfBusiness_TotalPremiumAmount_A',
    'Commercial Property': 'CommercialPropertyLineOfBusiness_PremiumAmount_A',
    'Inland Marine': 'CommercialInlandMarineLineOfBusiness_PremiumAmount_A',
    'Crime': 'CrimeLineOfBusiness_PremiumAmount_A',
    'Commercial Auto': 'CommercialVehicleLineOfBusiness_PremiumAmount_A',
    'Garage': 'GarageAndDealersLineOfBusiness_PremiumAmount_A',
    'Umbrella': 'CommercialUmbrellaLineOfBusiness_PremiumAmount_A',
    'Excess': 'ExcessLineOfBusiness_TotalPremiumAmount_A',
    'Professional Liability': 'ProfessionalLiabilityLineOfBusiness_TotalPremiumAmount_A',
  };

  for (const lob of lobSet) {
    if (lobCheckboxMap[lob]) {
      setCheckbox(lobCheckboxMap[lob]);
    }
  }

  // [FIX #3] Nature of Business — partial/contains match instead of exact
  const natureMap = {
    'Manufacturing': 'BusinessInformation_BusinessType_ManufacturingIndicator_A',
    'Contractor': 'BusinessInformation_BusinessType_ContractorIndicator_A',
    'Retail': 'BusinessInformation_BusinessType_RetailIndicator_A',
    'Wholesale': 'BusinessInformation_BusinessType_WholesaleIndicator_A',
    'Service': 'BusinessInformation_BusinessType_ServiceIndicator_A',
    'Office': 'BusinessInformation_BusinessType_OfficeIndicator_A',
    'Restaurant': 'BusinessInformation_BusinessType_RestaurantIndicator_A',
    'Apartments': 'BusinessInformation_BusinessType_ApartmentsIndicator_A',
    'Condominiums': 'BusinessInformation_BusinessType_CondominiumsIndicator_A',
    'Institutional': 'BusinessInformation_BusinessType_InstitutionalIndicator_A',
  };
  if (insured?.NatureOfBusiness) {
    const nob = insured.NatureOfBusiness.toLowerCase();
    for (const [key, fieldName] of Object.entries(natureMap)) {
      if (nob.includes(key.toLowerCase())) {
        setCheckbox(fieldName);
        break;
      }
    }
  }

  // ================================================================
  // PAGES 2–5: Require auth + SharePoint data
  // ================================================================
  if (getToken && insured?.id) {
    const token = await getToken();
    const insuredId = String(insured.id);

    // [FIX #7/#8/#9] Fetch ALL needed lists — added GeneralQuestions, CarrierHistory, LossHistory
    const [contacts, buildings, additionalInterests, policies, generalQuestions, carrierHistory, lossHistory] = await Promise.all([
      getItems(token, FIG_LISTS.CONTACTS),
      getItems(token, FIG_LISTS.BUILDINGS),
      getItems(token, FIG_LISTS.ADDITIONAL_INTERESTS),
      getItems(token, FIG_LISTS.POLICIES),
      getItems(token, FIG_LISTS.GENERAL_QUESTIONS),
      getItems(token, FIG_LISTS.CARRIER_HISTORY),
      getItems(token, FIG_LISTS.LOSS_HISTORY),
    ]);

    const myContacts = contacts.filter(c => String(c.InsuredId) === insuredId);
    const myBuildings = buildings.filter(b => String(b.InsuredId) === insuredId);
    const myPolicies = policies.filter(p => String(p.InsuredId) === insuredId);
    const myGQ = generalQuestions.filter(q => String(q.InsuredId) === insuredId);
    const myCH = carrierHistory.filter(c => String(c.InsuredId) === insuredId);
    const myLH = lossHistory.filter(l => String(l.InsuredId) === insuredId);

    // --- PAGE 1 continued: LOB premium lines from current policies only ---
    // Newest current row per LOB wins; prior-term (Expired) rows are ignored.
    const premiumWritten = new Set();
    for (const p of sortByEffectiveDesc(myPolicies.filter(isCurrentPolicy))) {
      if (p.LOB && lobSet.has(p.LOB) && p.Premium != null && lobPremiumMap[p.LOB] && !premiumWritten.has(p.LOB)) {
        setTextField(lobPremiumMap[p.LOB], String(p.Premium));
        premiumWritten.add(p.LOB);
      }
    }

    // --- PAGE 1 continued: Other Named Insureds (_B, _C) ---
    const otherNamedInsureds = additionalInterests.filter(
      ai => String(ai.InsuredId) === insuredId && (ai.InterestType || '').toLowerCase() === 'other named insured'
    );
    const oniSuffixes = ['_B', '_C'];

    for (let i = 0; i < Math.min(otherNamedInsureds.length, 2); i++) {
      const oni = otherNamedInsureds[i];
      const s = oniSuffixes[i];
      const notes = oni.Notes || '';

      const feinMatch = notes.match(/FEIN\s+([\d-]+)/i);
      const fein = feinMatch ? feinMatch[1] : '';

      setTextField(`NamedInsured_FullName${s}`, oni.EntityName || '');
      setTextField(`NamedInsured_MailingAddress_LineOne${s}`, oni.Address || '');
      setTextField(`NamedInsured_MailingAddress_CityName${s}`, oni.City || '');
      setTextField(`NamedInsured_MailingAddress_StateOrProvinceCode${s}`, oni.State || '');
      setTextField(`NamedInsured_MailingAddress_PostalCode${s}`, oni.Zip || '');
      setTextField(`NamedInsured_TaxIdentifier${s}`, fein);

      if (notes.includes('Corporation')) {
        setCheckbox(`NamedInsured_LegalEntity_CorporationIndicator${s}`);
      } else if (notes.includes('LLC')) {
        setCheckbox(`NamedInsured_LegalEntity_LimitedLiabilityCorporationIndicator${s}`);
      } else if (notes.includes('Trust')) {
        setCheckbox(`NamedInsured_LegalEntity_TrustIndicator${s}`);
      }
    }
    console.log(`[ACORD 125] Other Named Insureds: ${otherNamedInsureds.length} mapped`);

    // ================================================================
    // PAGE 3: Additional Interest (non-ONI roles)
    // ================================================================
    // Mirrors the ONI filter pattern (line ~261 above): scope to this
    // insured's records and exclude the ONIs handled on Page 1. The PDF has
    // capacity for ONE inline AI block; >1 record logs a warning and only the
    // first is rendered. Multi-AI overflow → ACORD 45 attachment is a
    // separate fix.
    const nonOniAIs = additionalInterests.filter(
      ai => String(ai.InsuredId) === insuredId && (ai.InterestType || '').toLowerCase() !== 'other named insured'
    );

    if (nonOniAIs.length > 0) {
      if (nonOniAIs.length > 1) {
        console.warn(`[ACORD 125] ${nonOniAIs.length} non-ONI Additional Interests present; only first rendered. ACORD 45 overflow not yet implemented.`);
      }
      const ai = nonOniAIs[0];

      setTextField('AdditionalInterest_FullName_A', ai.EntityName || '');
      setTextField('AdditionalInterest_MailingAddress_LineOne_A', ai.Address || '');
      setTextField('AdditionalInterest_MailingAddress_CityName_A', ai.City || '');
      setTextField('AdditionalInterest_MailingAddress_StateOrProvinceCode_A', ai.State || '');
      setTextField('AdditionalInterest_MailingAddress_PostalCode_A', ai.Zip || '');
      setTextField('AdditionalInterest_Primary_PhoneNumber_A', ai.Phone || '');
      setTextField('AdditionalInterest_Primary_EmailAddress_A', ai.Email || '');
      setTextField('AdditionalInterest_AccountNumberIdentifier_A', ai.ReferenceNumber || '');
      setTextField('AdditionalInterest_InterestRank_A', ai.Rank || '');
      setTextField('AdditionalInterest_Item_LocationProducerIdentifier_A', ai.LocationNumber || '');
      setTextField('AdditionalInterest_ItemDescription_A', ai.ItemDescription || '');
      setTextField('AdditionalInterest_InterestReasonDescription_A', ai.Notes || '');
      // NOTE: PDF widgets `AdditionalInterest_Primary_FaxNumber_A` and
      // `AdditionalInterest_LoanAmount_A` exist on the form but FIG_AdditionalInterests
      // has no Fax or LoanAmount columns. Wire here when the schema is extended.

      if (ai.EvidenceRequired === true || ai.EvidenceRequired === 'Yes') {
        setCheckbox('AdditionalInterest_CertificateRequiredIndicator_A');
      }

      const checkboxes = interestTypeToCheckboxNames(ai.InterestType);
      for (const cb of checkboxes) setCheckbox(cb);
      if (checkboxes.length === 0 && ai.InterestType) {
        console.warn(`[ACORD 125] InterestType "${ai.InterestType}" matched no known indicator. AI rendered without role checkbox.`);
      }
    }
    console.log(`[ACORD 125] Page 3: ${nonOniAIs.length} non-ONI Additional Interest record(s); ${nonOniAIs.length > 0 ? 1 : 0} rendered`);

    // ================================================================
    // PAGE 2: Contacts, Nature of Business, Buildings
    // ================================================================

    // [FIX #4] Contacts — up to 2 records (suffixes _A, _B)
    const contactSuffixes = ['_A', '_B'];
    for (let i = 0; i < Math.min(myContacts.length, 2); i++) {
      const c = myContacts[i];
      const s = contactSuffixes[i];
      setTextField(`NamedInsured_Contact_ContactDescription${s}`, c.ContactType || '');
      setTextField(`NamedInsured_Contact_FullName${s}`, c.ContactName || '');
      setTextField(`NamedInsured_Contact_PrimaryPhoneNumber${s}`, c.Phone || '');
      setTextField(`NamedInsured_Contact_PrimaryEmailAddress${s}`, c.Email || '');
      if (c.SecondaryEmail) {
        setTextField(`NamedInsured_Contact_SecondaryEmailAddress${s}`, c.SecondaryEmail);
      }
    }

    // [FIX #5] Buildings — expanded from 3 to 4 (suffixes _A, _B, _C, _D)
    // [FIX #6] Employee counts on ALL buildings, not just building 1
    const bldgSuffixes = ['_A', '_B', '_C', '_D'];

    for (let i = 0; i < Math.min(myBuildings.length, 4); i++) {
      const b = myBuildings[i];
      const s = bldgSuffixes[i];

      setTextField(`CommercialStructure_Location_ProducerIdentifier${s}`, '1');
      setTextField(`CommercialStructure_Building_ProducerIdentifier${s}`, String(i + 1));
      setTextField(`CommercialStructure_PhysicalAddress_LineOne${s}`, b.Address || '');
      setTextField(`CommercialStructure_PhysicalAddress_CityName${s}`, b.City || '');
      setTextField(`CommercialStructure_PhysicalAddress_CountyName${s}`, b.City || '');
      setTextField(`CommercialStructure_PhysicalAddress_StateOrProvinceCode${s}`, b.State || '');
      setTextField(`CommercialStructure_PhysicalAddress_PostalCode${s}`, b.Zip || '');
      setTextField(`CommercialStructure_AnnualRevenueAmount${s}`, b.AnnualRevenues != null ? String(b.AnnualRevenues) : '');
      setTextField(`BuildingOccupancy_OccupiedArea${s}`, b.SquareFootage != null ? String(b.SquareFootage) : '');
      setTextField(`Construction_BuildingArea${s}`, b.SquareFootage != null ? String(b.SquareFootage) : '');
      setTextField(`BuildingOccupancy_OperationsDescription${s}`, b.BuildingDescription || '');

      // Employee counts on every building
      setTextField(`BusinessInformation_FullTimeEmployeeCount${s}`, b.FullTimeEmployees != null ? String(b.FullTimeEmployees) : '');
      setTextField(`BusinessInformation_PartTimeEmployeeCount${s}`, b.PartTimeEmployees != null ? String(b.PartTimeEmployees) : '');

      // Owner/Tenant
      if ((b.OwnerOrTenant || '').toLowerCase() === 'owner') {
        setCheckbox(`CommercialStructure_InsuredInterest_OwnerIndicator${s}`);
      } else if ((b.OwnerOrTenant || '').toLowerCase() === 'tenant') {
        setCheckbox(`CommercialStructure_InsuredInterest_TenantIndicator${s}`);
      }
    }

    console.log(`[ACORD 125] Page 2: ${myContacts.length} contacts, ${myBuildings.length} buildings mapped`);

    // ================================================================
    // PAGE 3: General Information Y/N questions from FIG_GeneralQuestions
    // ================================================================

    // General Information Y/N questions — mapped to actual PDF field codes
    // ACORD 125 Page 3 uses CommercialPolicy_Question_XXXCode_A pattern
    //
    // FormType strings accepted:
    //   'ACORD125'    — written by ACORD125Page1.js handleSave (current intake)
    //   'GL-General'  — legacy/seed-fixture string
    //   '125-General' — legacy/seed-fixture string
    // Kept all three so intake-saved and legacy-seeded data both reach the PDF.
    const glGeneralQuestions = myGQ
      .filter(q => q.FormType === 'ACORD125' || q.FormType === 'GL-General' || q.FormType === '125-General')
      .sort((a, b) => Number(a.QuestionNumber) - Number(b.QuestionNumber));

    // [Fix #5b] Question-number → PDF Explanation_A field map. Used by the
    // dispatcher inside the GQ loop for questions whose Explanation is plain
    // prose (everything except Q2 / Q5, which have their own helpers above).
    // Q1a, Q1b, Q4, Q11, Q12 are intentionally absent — they store data that
    // doesn't map to a single Explanation_A widget on the PDF.
    const explanationFieldByQuestion = {
      '3': 'CommercialPolicy_AnyExposureToFlammableExplosivesChemicalsExplanation_A',
      '6': 'CommercialPolicy_PastLossesClaimsRelatingSexualAbuseDiscriminationNegligentHiringExplanation_A',
      '7': 'CommercialPolicy_PastFiveYearsAnyApplicantIndictedOrConvictedFraudBriberyArsonExplanation_A',
      '8': 'CommercialPolicy_UncorrectedFireCodeViolationExplanation_A',
      '9': 'CommercialPolicy_ForeclosureRepossessionBankruptcyExplanation_A',
      '10': 'CommercialPolicy_JudgementOrLienExplanation_A',
      '13': 'CommercialPolicy_ApplicantOtherBusinessVenturesCoverageNotRequestedExplanation_A',
      '14': 'CommercialPolicy_ApplicantOwnLeaseOperateDronesExplanation_A',
      '15': 'CommercialPolicy_ApplicantHireOthersOperateDronesExplanation_A',
    };

    // QuestionNumber → PDF Y/N character-code field map. Used as a fallback
    // when the regex-keyword match against QuestionText fails — required for
    // records where QuestionText is empty (ACORD125Page1.js handleSave at
    // line ~418 hardcodes QuestionText: ''). Each entry mirrors the .field
    // value of the regex-keyword entry that targets the same PDF widget;
    // when both maps are updated, keep them in sync.
    const questionNumberToYNField = {
      '1a': 'CommercialPolicy_Question_AAICode_A',
      '1b': 'CommercialPolicy_Question_AAJCode_A',
      '2':  'CommercialPolicy_Question_KAACode_A',
      '3':  'CommercialPolicy_Question_ABCCode_A',
      '4':  'CommercialPolicy_Question_AAHCode_A',
      '5':  'CommercialPolicy_Question_AACCode_A',
      '6':  'CommercialPolicy_Question_AADCode_A',
      '7':  'CommercialPolicy_Question_KABCode_A',
      '8':  'CommercialPolicy_Question_AAFCode_A',
      '9':  'CommercialPolicy_Question_KAKCode_A',
      '10': 'CommercialPolicy_Question_KALCode_A',
      '11': 'CommercialPolicy_Question_ABBCode_A',
      '12': 'CommercialPolicy_Question_KACCode_A',
      '13': 'CommercialPolicy_Question_KAMCode_A',
      '14': 'CommercialPolicy_Question_KANCode_A',
      '15': 'CommercialPolicy_Question_KAOCode_A',
    };

    // Keyword→PDF-field map for ACORD 125 Page 3 General Information (GL-General / 125-General).
    // Each SharePoint question's QuestionText is tested against these patterns; first match wins.
    // Ordered in the PDF's field-code sequence. Comments describe what the PDF field represents.
    const questionKeywordMap = [
      { field: 'CommercialPolicy_Question_AACCode_A', pattern: /declin|cancel|non[- ]?renew/i },                      // declined/cancelled/non-renewed
      { field: 'CommercialPolicy_Question_KAACode_A', pattern: /formal safety program|safety program|safety manual/i },// formal safety program
      { field: 'CommercialPolicy_Question_KAKCode_A', pattern: /bankruptc|foreclos|repossess/i },                     // bankruptcy/foreclosure/repossession
      { field: 'CommercialPolicy_Question_AAHCode_A', pattern: /other insurance|insurance.*same/i },                  // other insurance
      { field: 'CommercialPolicy_Question_ABCCode_A', pattern: /flammabl|explosiv|chemical/i },                       // flammables/explosives/chemicals
      { field: 'CommercialPolicy_Question_AADCode_A', pattern: /sexual|abuse|molest/i },                              // sexual abuse/molestation
      { field: 'CommercialPolicy_Question_KABCode_A', pattern: /arson/i },                                            // arson conviction
      { field: 'CommercialPolicy_Question_AAFCode_A', pattern: /fire code|code violation|uncorrected/i },             // fire code violations
      { field: 'CommercialPolicy_Question_AAJCode_A', pattern: /have any subsidiar|applicant.*subsidiar(y|ies)\s*\?/i }, // applicant HAS subsidiaries?
      { field: 'CommercialPolicy_Question_AAICode_A', pattern: /subsidiary of|subsidiar|affiliat|parent/i },             // applicant IS a subsidiary / parent
      { field: 'CommercialPolicy_Question_ABBCode_A', pattern: /\btrust\b|\btrustee\b/i },                            // trust
      { field: 'CommercialPolicy_Question_KACCode_A', pattern: /foreign/i },                                          // foreign operations
      { field: 'CommercialPolicy_Question_KAMCode_A', pattern: /other (business|ventur)/i },                          // other business ventures
      { field: 'CommercialPolicy_Question_KAOCode_A', pattern: /drone.*hir|hir.*drone/i },                                  // drones hired (others operate)
      { field: 'CommercialPolicy_Question_KANCode_A', pattern: /drone.*(own|lease|operate)|(own|lease|operate).*drone/i }, // drones owned/leased/operated
      { field: 'CommercialPolicy_Question_KALCode_A', pattern: /judg?ment|\blien\b/i },                               // judgement/lien
    ];

    for (const q of glGeneralQuestions) {
      const qText = q.QuestionText || q.Title || '';
      const qNum = q.QuestionNumber || '?';
      const match = questionKeywordMap.find(m => m.pattern.test(qText));

      // Resolve the Y/N PDF field via two paths, in priority order:
      //   1. Regex-keyword match against QuestionText (works for legacy
      //      GL-General / 125-General records that carry real QuestionText).
      //   2. QuestionNumber lookup (works for ACORD125-FormType records
      //      where intake's handleSave hardcodes QuestionText: '').
      // The warning fires only when BOTH paths fail.
      const ynField = match
        ? match.field
        : questionNumberToYNField[String(q.QuestionNumber || '').trim().replace(/^Q/i, '')];

      if (!ynField) {
        console.warn(`[ACORD 125] WARNING: Unmatched question Q${qNum}: "${qText}"`);
        continue;
      }

      // PDF fields are maxLength=1, so convert Yes/No → Y/N
      const raw = (q.Answer || '').trim();
      const val = raw.charAt(0).toUpperCase() || '';
      setTextField(ynField, val);

      const snippet = qText.slice(0, 40);
      console.log(`[ACORD 125] Q${qNum} "${snippet}" → ${ynField} = ${val}`);

      // [Fix #5b] Explanation dispatcher — three modes by question number:
      //   Q2  → comma-separated set of safety-program features (helper above)
      //   Q5  → pipe-delimited "<reason-set>|<free-text>" (helper above)
      //   else → plain prose into the matching CommercialPolicy_*Explanation_A
      // Gated on val === 'Y' so we don't paint explanations under No answers,
      // and on Explanation being non-empty.
      const dispatchKey = String(q.QuestionNumber || '').trim().replace(/^Q/i, '');
      const explanationText = (q.Explanation || '').trim();
      if (val === 'Y' && explanationText) {
        if (dispatchKey === '2') {
          writeQ2SafetyProgramFromExplanation(explanationText);
        } else if (dispatchKey === '5') {
          writeQ5CancellationFromExplanation(explanationText);
        } else if (explanationFieldByQuestion[dispatchKey]) {
          setTextField(explanationFieldByQuestion[dispatchKey], explanationText);
        }
      }
    }
    // TODO: occurrence/resolution sub-fields on Q8/Q9/Q10 stay UNMAPPED.
    // Recon (docs/acord125_fix5_recon_2026-04-25.md) confirmed Explanation
    // is free-form prose for these questions; structured parsing would produce
    // brittle output. Wire when intake captures occurrence/resolution as
    // separate FIG_GeneralQuestions columns.

    // Safety program fields from WC-General questions
    const wcQuestions = myGQ
      .filter(q => q.FormType === 'WC-General')
      .sort((a, b) => Number(a.QuestionNumber) - Number(b.QuestionNumber));

    for (const q of wcQuestions) {
      const text = (q.QuestionText || q.Title || '').toLowerCase();
      if (text.includes('formal safety program') && q.Answer === 'Yes') {
        setCheckbox('CommercialPolicy_FormalSafetyProgram_SafetyManualIndicator_A');
      }
      if (text.includes('return-to-work') && q.Answer === 'Yes') {
        setCheckbox('CommercialPolicy_FormalSafetyProgram_SafetyPositionIndicator_B');
      }
      if (text.includes('osha') && q.Answer === 'No') {
        setCheckbox('CommercialPolicy_FormalSafetyProgram_OSHAIndicator_B');
      }
      if (text.includes('drug-free') && q.Answer === 'Yes') {
        setCheckbox('CommercialPolicy_FormalSafetyProgram_MonthlyMeetingsIndicator_B');
      }
    }

    console.log(`[ACORD 125] Page 3: ${glGeneralQuestions.length} GL-General questions, ${wcQuestions.length} WC-General questions mapped`);

    // ================================================================
    // PAGE 4: Prior Carrier History + Loss History
    // ================================================================

    // Prior Carrier Information — ACORD 125 organizes by LOB columns (GL, Auto, Property, Other)
    // Each LOB column has _A, _B, _C suffixes for up to 3 policy years
    const lobColumnMap = {
      'General Liability': 'GeneralLiability',
      'Commercial General Liability': 'GeneralLiability',
      'Commercial Auto': 'Automobile',
      'Commercial Property': 'Property',
    };

    // Group carrier history by LOB column
    const chByLob = {};
    for (const ch of myCH) {
      const colKey = lobColumnMap[ch.LOB] || 'OtherLine';
      if (!chByLob[colKey]) chByLob[colKey] = [];
      chByLob[colKey].push(ch);
    }

    // Sort each group by policy year descending, then map up to 3 per column
    const yearSuffixes = ['_A', '_B', '_C'];
    for (const [colKey, records] of Object.entries(chByLob)) {
      const sorted = records.sort((a, b) => (b.PolicyYear || '').localeCompare(a.PolicyYear || ''));
      for (let i = 0; i < Math.min(sorted.length, 3); i++) {
        const ch = sorted[i];
        const s = yearSuffixes[i];
        setTextField(`PriorCoverage_${colKey}_InsurerFullName${s}`, ch.Carrier || '');
        setTextField(`PriorCoverage_${colKey}_PolicyNumberIdentifier${s}`, ch.PolicyNumber || '');
        setTextField(`PriorCoverage_${colKey}_EffectiveDate${s}`, ch.EffectiveDate || '');
        setTextField(`PriorCoverage_${colKey}_ExpirationDate${s}`, ch.ExpirationDate || '');
        setTextField(`PriorCoverage_${colKey}_TotalPremiumAmount${s}`, ch.Premium != null ? String(ch.Premium) : '');
      }
    }

    // Shared YEAR column on the left — one value per row suffix, independent
    // of LOB column. Walk distinct PolicyYear values across all carrier
    // history records, sort newest first, assign _A/_B/_C.
    const distinctYears = [...new Set(myCH.map(ch => ch.PolicyYear).filter(Boolean))]
      .sort((a, b) => String(b).localeCompare(String(a)));
    for (let i = 0; i < Math.min(distinctYears.length, 3); i++) {
      setTextField(`PriorCoverage_PolicyYear${yearSuffixes[i]}`, String(distinctYears[i]));
    }

    console.log(`[ACORD 125] Page 4: ${myCH.length} carrier history records mapped across ${Object.keys(chByLob).length} LOB columns`);

    // [FIX #9] Loss History from FIG_LossHistory (up to 3 rows — PDF has _A/_B/_C only)
    // Only check "No Prior Losses" when there truly are zero loss records
    if (myLH.length === 0) {
      setCheckbox('LossHistory_NoPriorLossesIndicator_A');
    } else {
      const sortedLH = [...myLH].sort((a, b) =>
        (b.DateOfOccurrence || '').localeCompare(a.DateOfOccurrence || '')
      );
      const lhSuffixes = ['_A', '_B', '_C'];

      for (let i = 0; i < Math.min(sortedLH.length, 3); i++) {
        const l = sortedLH[i];
        const s = lhSuffixes[i];
        setTextField(`LossHistory_OccurrenceDate${s}`, l.DateOfOccurrence || '');
        setTextField(`LossHistory_ClaimDate${s}`, l.DateOfClaim || '');
        setTextField(`LossHistory_OccurrenceDescription${s}`, l.Description || '');
        setTextField(`LossHistory_LineOfBusiness${s}`, l.LOB || '');
        setTextField(`LossHistory_PaidAmount${s}`, l.AmountPaid != null ? String(l.AmountPaid) : '');
        setTextField(`LossHistory_ReservedAmount${s}`, l.AmountReserved != null ? String(l.AmountReserved) : '');
        // Claim open status is a TEXT field on the PDF (not a checkbox).
        // Coerce: null/undefined → ''; true / 'Yes' → 'Y'; anything else → 'N'.
        const claimOpenCode = l.ClaimOpen == null
          ? ''
          : (l.ClaimOpen === true || l.ClaimOpen === 'Yes' ? 'Y' : 'N');
        setTextField(`LossHistory_ClaimStatus_OpenCode${s}`, claimOpenCode);
      }
    }
    setTextField('LossHistory_InformationYearCount_A', '5');
    console.log(`[ACORD 125] Page 4: ${myLH.length} loss history records mapped`);

    // ================================================================
    // PAGE 5: Signature
    // ================================================================
    // Applicant signature + date intentionally left blank — client signs and
    // dates by hand at bind time. Producer fields are handled elsewhere.
  }

  // 5. Save and trigger download
  const filledBytes = await pdfDoc.save({ updateFieldAppearances: false });
  const blob = new Blob([filledBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ACORD_125_${(insured?.CompanyName || 'insured').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
